import { useCallback, useState } from "react";

import BadgeSelect from "@/components/ui/BadgeSelect";
import Checkbox from "@/components/ui/checkbox";
import { Divider } from "@/components/ui/Divider";
import SearchBox from "@/components/ui/searchBox";
import type { License } from "@/types/dataset";

import {
  type DatasetExplorerSessionStorageData,
  DEFAULT_SESSION_DATA
} from ".";

const TEST_ID_PREFIX = "data-sources-list";

const LICENSE_OPTIONS: { label: string; value: License }[] = [
  { label: "All", value: "all" },
  { label: "Regular", value: "regular" },
  { label: "Premium", value: "premium" }
];

export type DataSourceProvider = {
  id: string;
  label: string;
  icon?: React.ReactNode;
};

export type DataSourceListProps = {
  dataSourceProviders: DataSourceProvider[];
  selectedDataSources: string[];
  license: License;
  setSelectedDataSources: (value: string[]) => void;
  setLicense: (value: License) => void;
  sessionStorageData: DatasetExplorerSessionStorageData;
  setSessionItem: (data: DatasetExplorerSessionStorageData) => void;
};

export const DataSourceList = ({
  dataSourceProviders,
  selectedDataSources,
  license,
  setSelectedDataSources,
  setLicense,
  sessionStorageData,
  setSessionItem
}: DataSourceListProps) => {
  const [searchDataSource, setSearchDataSource] = useState<string>(
    sessionStorageData?.searchDataSource ??
      DEFAULT_SESSION_DATA.searchDataSource
  );

  const setSearchDataSourceQuery = useCallback(
    (value: string) => {
      setSearchDataSource(value);
      setSessionItem({
        ...sessionStorageData,
        searchDataSource: value
      });
    },
    [setSearchDataSource, sessionStorageData, setSessionItem]
  );

  const handleCheckboxChange = useCallback(
    (name: string) => {
      const newSelectedDataSources = selectedDataSources.includes(name)
        ? selectedDataSources.filter((item) => item !== name)
        : [...selectedDataSources, name];
      setSelectedDataSources(newSelectedDataSources);
      setSessionItem({
        ...sessionStorageData,
        providerId: newSelectedDataSources.join(","),
        currentPage: 1
      });
    },
    [
      selectedDataSources,
      setSelectedDataSources,
      sessionStorageData,
      setSessionItem
    ]
  );

  const filteredProviders = dataSourceProviders.filter(({ label }) =>
    label.toLowerCase().includes(searchDataSource.toLowerCase())
  );

  return (
    <>
      <SearchBox
        disabled={false}
        value={searchDataSource}
        onChange={(e) => setSearchDataSourceQuery(e.target.value)}
        placeHolder="Search data source"
        className="max-w-xs py-3 my-4 text-[14px]"
        testId={TEST_ID_PREFIX}
      />
      <div className="flex flex-col gap-2 overflow-y-auto max-h-52">
        {filteredProviders.map(({ id, label, icon }) => (
          <div key={id} className="flex items-center gap-2">
            <Checkbox
              id={id}
              onChange={() => handleCheckboxChange(id)}
              checked={selectedDataSources.includes(id)}
              className="w-5 h-5"
              data-testId={`${TEST_ID_PREFIX}-checkbox-${id}`}
            />
            <label
              htmlFor={id}
              className="flex items-center gap-2 text-sm font-normal text-txtBody"
            >
              {icon && (
                <div className="flex items-center justify-center w-8 h-8 border border-[#E5E7EB] rounded-lg">
                  {icon}
                </div>
              )}
              <span className="text-sm font-semibold">{label}</span>
            </label>
          </div>
        ))}
        {filteredProviders.length === 0 && (
          <p className="text-sm text-grey">No data source found</p>
        )}
      </div>
      <Divider className="bg-gainsboro w-[100%] h-[1px] mt-4" />
      <BadgeSelect
        label="License"
        className="mt-3"
        showDivider
        data={LICENSE_OPTIONS}
        itemSelected={license}
        onSelect={(data) => {
          setLicense(data as License);
        }}
      />
    </>
  );
};
